import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { TItemWithChildren } from '@fi-shared/fi-select-search/select-search-base';
import { MatOptionSelectionChange } from '@angular/material/core';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-connected-dropdown-items',
    templateUrl: './fi-connected-dropdown-items.component.html',
    styleUrl: './fi-connected-dropdown-items.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-connected-dropdown-items' },
    standalone: false,
})
export class FiConnectedDropdownItemsComponent<T> {
    @Input({ required: true }) items: TItemWithChildren<T>[];
    @Input({ required: true }) valuePropName: string;
    @Input({ required: true }) getPaddingFn: (item: T) => string | number;
    @Input({ required: true }) itemDepthPropertyName: string;
    @Input({ required: true }) displayProperty: string;
    @Input() itemsType: 'option' | 'menu-button' = 'option';

    @Input({ required: true }) selectedItems: T[] = [];
    @Output() onSelectionChange = new EventEmitter<{ item: T; $event: MatOptionSelectionChange }>();
    @Output() onItemClick = new EventEmitter<{ item: T; $event: Event }>();

    @Input() parentIdPropName: string;

    @Input() getOptionTitleFn: (item: T) => string;
    @Input() getItemDisplayValueFn: (item: T) => string;
    @Input() isOptionDisabledFn: (item: T) => boolean;

    @Input() expandedMainItemIds: number[] = [];

    constructor(private cdr: ChangeDetectorRef) {}

    get selectedItemIds(): number[] {
        return this.selectedItems
            .filter((selectedItem: T) => isDefined(selectedItem))
            .map((selectedItem) => selectedItem[this.valuePropName]);
    }

    private readonly baseToggleButtonRightOffset: number = 30;
    private readonly baseToggleButtonLeftOffset: number = 8;

    getToggleButtonLeftOffset(item: TItemWithChildren<T>): string {
        const parentPadding: number | string = this.getPaddingFn(item);
        if (!isDefined(parentPadding)) return this.baseToggleButtonLeftOffset + 'px';

        if (typeof parentPadding === 'number') {
            return parentPadding - this.baseToggleButtonRightOffset + 'px';
        }
        if (typeof parentPadding === 'string') {
            return +parentPadding.replace('px', '') - this.baseToggleButtonRightOffset + 'px';
        }
        return this.baseToggleButtonLeftOffset + 'px';
    }

    isItemShown(item: TItemWithChildren<T>): boolean {
        if (!item.hasOwnProperty(this.parentIdPropName)) return true;

        return this.expandedMainItemIds.includes(item[this.parentIdPropName]);
    }

    itemSelectionChange(item: T, $event: MatOptionSelectionChange) {
        this.onSelectionChange.emit({ item, $event });
    }

    getOptionTitle(item: T): string {
        return this.getOptionTitleFn?.(item) ?? item[this.displayProperty];
    }

    getItemDisplayValue(item: T): string {
        return this.getItemDisplayValueFn?.(item) ?? item[this.displayProperty];
    }

    isItemSelected(item: T): boolean {
        return this.selectedItemIds.includes(item[this.valuePropName]);
    }

    isOptionDisabled(item: T): boolean {
        return this.isOptionDisabledFn?.(item) ?? false;
    }

    isMainItem(item: TItemWithChildren<T>): boolean {
        return !!item.ChildItems?.length;
    }

    toggleChildrenVisibility(item: TItemWithChildren<T>, $event: MouseEvent) {
        $event.preventDefault();
        $event.stopPropagation();
        const itemValue: number = item[this.valuePropName];

        if (this.expandedMainItemIds.includes(itemValue)) {
            this.collapseFoldableItems(item);
        } else {
            this.expandedMainItemIds.push(itemValue);
        }
        setTimeout(() => this.cdr.markForCheck());
    }

    expandItems(
        items: TItemWithChildren<T>[],
        args?: {
            collapsePreviouslyExpandedItems?: boolean;
        },
    ) {
        if (args?.collapsePreviouslyExpandedItems) {
            this.expandedMainItemIds = [];
        }

        const flattenItems = fiItemsFlatten(items);
        flattenItems
            .filter((item) => item.ChildItems?.length > 0)
            .forEach((item: TItemWithChildren<T>) => {
                const itemValue: number = item[this.valuePropName];
                if (!this.expandedMainItemIds.includes(itemValue)) {
                    this.expandedMainItemIds.push(itemValue);
                }
            });
    }

    private collapseFoldableItems(item: TItemWithChildren<T>) {
        const itemValue: number = item[this.valuePropName];
        if (this.expandedMainItemIds.includes(itemValue)) {
            this.expandedMainItemIds.splice(this.expandedMainItemIds.indexOf(itemValue), 1);
        }
        const isChildrenFn = (flattenItem: TItemWithChildren<T>) => +flattenItem[this.parentIdPropName] === +itemValue;
        const visibleChildren: TItemWithChildren<T>[] = this.getFlatFilteredItems(isChildrenFn);

        if (visibleChildren?.length) {
            visibleChildren.forEach((childItem: TItemWithChildren<T>) => this.collapseFoldableItems(childItem));
        }
    }

    getItemDepthClass(item: TItemWithChildren<T>): { [key: string]: boolean } {
        return {
            ['item-level-' + item[this.itemDepthPropertyName]]: item.hasOwnProperty(this.itemDepthPropertyName),
            'nested-option': this.isMainItem(item) || item.hasOwnProperty(this.parentIdPropName),
            expanded: this.isItemExpanded(item),
        };
    }

    getItemOptionWrapperClasses(item: TItemWithChildren<T>): { [key: string]: boolean } {
        return {
            'main-option': this.isMainItem(item),
            'last-child': !this.isMainItem(item) && this.isOptionWrapperLastChild(item),
        };
    }

    private isOptionWrapperLastChild(item: TItemWithChildren<T>): boolean {
        if (!item.hasOwnProperty(this.parentIdPropName)) return false;

        const isSiblingFn = (flattenItem: TItemWithChildren<T>) =>
            +flattenItem[this.parentIdPropName] === item[this.parentIdPropName];
        const visibleSiblings: TItemWithChildren<T>[] = this.getFlatFilteredItems(isSiblingFn);
        const currentItemIndex: number = visibleSiblings.findIndex((childItem: TItemWithChildren<T>) => {
            return childItem[this.valuePropName] === item[this.valuePropName];
        });
        return currentItemIndex === visibleSiblings.length - 1;
    }

    private getFlatFilteredItems(filterFn: (item: TItemWithChildren<T>) => boolean): TItemWithChildren<T>[] {
        return fiItemsFlatten(this.items as TItemWithChildren<T>[]).filter(filterFn);
    }

    private readonly optionWrapperIdPrefix: string = 'item-option-';
    getOptionWrapperId(item: T): string {
        return this.optionWrapperIdPrefix + item[this.valuePropName];
    }

    isItemExpanded(item: TItemWithChildren<T>): boolean {
        return this.expandedMainItemIds.includes(item[this.valuePropName]);
    }

    private readonly afterHeightOffset: number = 14;
    private readonly minOptionHeight: number = 24;
    getAfterHeight(item: TItemWithChildren<T>): number {
        const itemValue: string = item[this.valuePropName].toString();
        const isChildrenFn = (flattenItem: TItemWithChildren<T>) => +flattenItem[this.parentIdPropName] === +itemValue;
        const visibleChildren: TItemWithChildren<T>[] = this.getFlatFilteredItems(isChildrenFn);

        if (!visibleChildren?.length || !this.expandedMainItemIds.includes(+itemValue)) return 0;

        const lastChild: TItemWithChildren<T> = visibleChildren[visibleChildren.length - 1];

        const currentEl: HTMLElement = document.getElementById(this.getOptionWrapperId(item));
        const lastChildEl: HTMLElement = document.getElementById(this.getOptionWrapperId(lastChild));
        if (isNullOrUndefined(currentEl) || isNullOrUndefined(lastChildEl)) return 0;

        return (
            Math.abs(lastChildEl.getBoundingClientRect().top - currentEl.getBoundingClientRect().bottom) +
            this.afterHeightOffset +
            currentEl.getBoundingClientRect().height -
            this.minOptionHeight
        );
    }

    handleItemClick(item: T, $event: Event) {
        this.onItemClick.emit({ item, $event });
    }
}
